<template>
  <div>
    <FilesNavbar :selected-files="selectedFiles" :breadcrumb="breadcrumb" :files-number="filesNumber" @go-back="goBack()"
      @select-all="selectAll" @add-folder="showAddFolderModal()" @reload="refetchAll" />
    <div v-if="hasObjects" class="d-flex">
      <v-jstree ref="tree_view_data" :data="asyncData" :async="loadData" text-field-name="label" value-field-name="id"
        style="min-width: 20%; max-width: 35%;" class="pt-3" @item-click="clickTreeView" />

      <component :is="isGrid ? 'FilesGrid' : 'FilesTable'" :files="files" :selected-files="selectedFiles" class="mt-3"
        @select="selectFile" @open-folder="openFolder" @loading="loading" />
    </div>
    <FolderAddModal class="myModal" @added="refetchAll" />
    <RenameModal :files="files" @renamed="refetchAll" @loading="loading" />
    <ExpirationModal :files="files" @expiration-updated="onExpirationUpdated" @stale="refetchAll" />
    <UploadFileModal class="myModal" @loading="loading" @uploaded="refetchAll" />
    <UploadFolderTreeModal class="myModal" @loading="loading" @uploaded="refetchAll" />

    <RemoveModal :selected-files="selectedFiles" :files="files" @removed="refetchAll" @reloadDirs="refreshNode"
      @loading="loading" />
    <MoveModal :selected-files="selectedFiles" @moved="refetchAll" />
    <DetailModal :files="files" @moved="refetchAll" />
    <ShareModal :files="files" @loading="loading" />
    <NewCopyModal :files="files" @refresh="refetchAll" @loading="loading" />
    <MoveModal id="multimove-modal" :selected-files="selectedFiles" :multiple="true" @moved="refetchAll" />
    <RemoveModal id="multidelete-modal" :files="files" :selected-files="selectedFiles" :multiple="true"
      @removed="refetchAll" @loading="loading" />
    <MoveModal
      id="restore-modal"
      :trashed="true"
      :trashed-ids="selectedTrashFiles"
      @restored="onRestored"
    />
    <StagingModal />
    <TrashModal ref="trashModal" @trash-select="selectTrashFile" @empty-trash-selected="emptyTrashSelected" />
    <DownloadsModal />
    <RemoveModal id="forceDelete-modal" :files="files" :selected-files="selectedFiles" :permanent="true"
      @removed="refetchAll" @loading="loading" />
  </div>
</div></template>

<script>
import useMyStore from '@/services/useMyStore'
import { computed } from '@vue/composition-api'
import FilesBackButton from '@/layouts/components/FilesBackButton.vue'
import FilesNavbar from '@/layouts/components/FilesNavbar.vue'
import FolderAddModal from '@/layouts/components/modals/FolderAddModal.vue'
import { RepositoryFactory } from '@/api/RepositoryFactory'
import RenameModal from '@/layouts/components/modals/RenameModal.vue'
import RemoveModal from '@/layouts/components/modals/RemoveModal.vue'
import TrashModal from '@/layouts/components/modals/TrashModal.vue'
import DownloadsModal from '@/layouts/components/modals/DownloadsModal.vue'
import ExpirationModal from '@/layouts/components/modals/ExpirationModal.vue'
import VJstree from 'vue-jstree'
import MoveModal from '@/layouts/components/modals/MoveModal.vue'
import DetailModal from '@/layouts/components/modals/DetailModal.vue'
import ShareModal from '@/layouts/components/modals/ShareModal.vue'
import AddUserModal from '@/layouts/components/modals/AddUserModal.vue'

import 'vue-loading-overlay/dist/vue-loading.css'
import Loading from 'vue-loading-overlay'
import moment from 'moment'
import tenantRepository from '@/api/tenantRepository'
import NewCopyModal from '@/layouts/components/modals/NewCopyModal.vue'
import * as commonHelpers from '@/helpers/common-helpers'
import StagingModal from '@/layouts/components/modals/StagingModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import UploadFileModal from './modals/UploadFileModal.vue'
import FilesGrid from './FilesGrid.vue'

import FilesTable from './FilesTable.vue'
import UploadFolderTreeModal from './modals/UploadFolderTreeModal.vue'

const ObjectRepository = RepositoryFactory.get('objects')

export default {
  name: 'Files',
  components: {
    StagingModal,
    NewCopyModal,
    Loading,
    FolderAddModal,
    FilesNavbar,
    FilesBackButton,
    FilesTable,
    FilesGrid,
    RenameModal,
    ExpirationModal,
    UploadFileModal,
    RemoveModal,
    MoveModal,
    VJstree,
    DetailModal,
    ShareModal,
    TrashModal,
    DownloadsModal,
    AddUserModal,
    UploadFolderTreeModal
},
  props: {
    fatherId: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    const { viewMode, currentFolder, currentObject } = useMyStore()
    const isGrid = computed(() => viewMode.value === 'grid')

    return {
      isGrid,
      currentFolder,
      currentObject,
    }
  },
  data() {
    return {
      loadingShow: false,
      // eslint-disable-next-line radix
      id: parseInt(this.$route.params.id) || null,
      viewMode: 'FilesTable',
      files: [],
      isFolderModalOpen: false,
      breadcrumb: [],
      asyncData: [],
      selectedTrashFiles: [],
      hasObjects: true,
    }
  },
  computed: {
    selectedFiles() {
      return this.files.filter(file => file.selected === true)
    },
    filesNumber() {
      return this.files.length
    },
  },
  watch: {
    currentFolder: {
      handler(val) {
        this.files = []
        this.fetchFiles()
      },
      deep: true,
    },
  },
  async created() {
    this.$emit('loading', true)
    this.currentFolder = this.id
    await this.fetchFiles()
    await this.changeTitle()
    this.$emit('loading', false)
  },
  methods: {
    refetchAll() {
      // this.refreshNode()
      this.fetchFiles()
    },
    loadData(oriNode, resolve) {
      if (this.hasObjects) {
        const id = oriNode.data.id ? oriNode.data.id : null
        ObjectRepository.getDirs(id)
          .then(res => {
            resolve(res.data.data.dirs)
          })
      }
    },
    refreshNode() {
      this.asyncData = [this.$refs.tree_view_data.initializeLoading()]
      this.$refs.tree_view_data.handleAsyncLoad(
        this.asyncData,
        this.$refs.tree_view_data,
      )
    },
    sortFiles(list) {
      const sorted = [...list].sort((a, b) => {
        const nameA = a.label.toUpperCase() // ignore upper and lowercase
        const nameB = b.label.toUpperCase() // ignore upper and lowercase
        if (nameA < nameB) {
          return -1
        }
        if (nameA > nameB) {
          return 1
        }

        // names must be equal
        return 0
      })

      const fo = sorted.filter(f => f.is_folder === 1)
      const fi = sorted.filter(f => f.is_folder === 0)
      return fo.concat(fi)
    },
    normalizeFile(file) {
      return {
        ...file,
        created_at: this.formatDatetime(file.created_at),
        updated_at: this.formatDatetime(file.updated_at),
        downloaded_at: file.downloaded_at == null
          ? ''
          : this.formatDatetime(file.downloaded_at),
      }
    },
    async fetchFiles() {
      this.loading(true)
      try {
        const { data } = await ObjectRepository.index({
          father_id: this.currentFolder,
        })
        console.log(data)
        if (data.data.objects.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Info:',
              text: 'Nessun file trovato!',
              icon: 'Info',
              variant: 'info',
            },
          },
            {
              position: 'top-center',
            })
        }
        if (data.code && this.hasObjects) {
          this.files = this.sortFiles(data.data.objects).map(f => this.normalizeFile(f))
          this.breadcrumb = data.data.breadcrumb
        }
      } catch (error) {
        console.log(error)
      }

      this.loading(false)
    },
    openFolder(file) {
      this.goTo(file.id)
    },
    selectFile(file) {
      this.$set(file, 'selected', !file.selected)
    },
    onExpirationUpdated(update) {
      const row = this.files.find(f => f.id === update.id)
      if (row) {
        this.$set(row, 'expiration_date', update.expiration_date)
      }
    },
    showAddFolderModal() {
      this.isFolderModalOpen = true
    },
    loading(show) {
      this.$emit('loading', show)
    },
    clickTreeView(node) {
      this.goTo(node.model.id)
    },
    formatDatetime(time) {
      return moment(time)
        .format('DD-MM-YYYY HH:mm')
    },
    async changeTitle() {
      const title = localStorage.getItem('title')
      if (title == null) {
        const { data } = await tenantRepository.info()
        localStorage.setItem('title', data.company)
        if (data) {
          document.title = `Archistar - ${data.company}`
        }
      } else {
        document.title = `Archistar - ${title}`
      }
    },
    goTo(id) {
      if (id == null) {
        this.currentFolder = 0
        this.commonHelpers.checkCurrentRouteAndRedirect(
          this.$router,
          '/files/',
        )
      } else {
        this.commonHelpers.checkCurrentRouteAndRedirect(
          this.$router,
          `/files/${id}`,
        )
        this.currentFolder = id
      }
    },
    goBack() {
      const file = this.breadcrumb.find(el => el.id === this.currentFolder)
      this.goTo(file.father_id)
    },
    selectAll(val) {
      this.files.forEach(file => {
        this.$set(file, 'selected', val)
      })
    },
    selectTrashFile(id) {
      if (this.selectedTrashFiles.includes(id)) {
        this.selectedTrashFiles = this.selectedTrashFiles.filter(i => i !== id)
      } else {
        this.selectedTrashFiles.push(id)
      }
    },
    emptyTrashSelected() {
      this.selectedTrashFiles = []
    },
    onRestored({ restoredObjects }) {
      const ids = restoredObjects.map(o => o.id)
      const removed = this.$refs.trashModal ? this.$refs.trashModal.removeRestored(ids) : []
      this.selectedTrashFiles = this.selectedTrashFiles.filter(id => !ids.includes(id))

      const allInCurrentFolder = restoredObjects.length > 0 && restoredObjects.every(o => {
        if (Number(o.father_id) !== Number(this.currentFolder)) {
          return false
        }
        return removed.some(entry => entry.id === o.id)
      })

      if (allInCurrentFolder) {
        const rows = restoredObjects.map(o => {
          const entry = removed.find(e => e.id === o.id)
          return this.normalizeFile({ ...entry, ...o, selected: false })
        })
        this.files = this.sortFiles([...this.files.filter(f => !ids.includes(f.id)), ...rows])
      } else {
        this.refetchAll()
      }

      if (restoredObjects.some(o => Number(o.is_folder) === 1) && this.$refs.tree_view_data) {
        this.refreshNode()
      }
    },
  },
}
</script>

<style>
[dir] .tree-default .tree-selected {
  background-color: rgba(164, 157, 245, 0.4) !important;
}

.tree-icon .tree-ocl {
  background-color: #1ab7ea !important;
}

.tree-hovered {
  background-color: rgba(164, 157, 245, 0.4) !important;
}
</style>
